/**
 * ============================================================================
 * DOCUMENTS STORE - CACHE RETRIEVAL BY FOLDER
 * ============================================================================
 * 
 * Functions for retrieving cached documents filtered by folder hierarchy.
 * 
 * @module documents/cache/byFolder
 */

import { getAllSubfolderIds } from "../../folderStore";
import { documentStore } from "../store";
import type { DocumentItem } from "../types";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if an item is in the root (no folder assigned).
 */
function isInRoot(folder: string | null | undefined): boolean {
  return !folder || folder === null || folder === '';
}

/**
 * Check if an item's folder is in a given set of folder IDs.
 */
function isInFolderSet(itemFolder: string | null | undefined, folderIds: string[]): boolean {
  return !!itemFolder && folderIds.includes(itemFolder);
}

// ============================================================================
// CACHE RETRIEVAL BY FOLDER
// ============================================================================

/**
 * Get cached documents for a specific game and folder (including subfolders).
 * 
 * Behavior:
 * - If folderId is null/undefined/empty: Returns documents in root (no folder)
 * - If folderId is provided: Returns documents in that folder AND all subfolders
 * 
 * @param gameId - The game ID to filter by
 * @param folderId - The folder ID to filter by (null/undefined for root items)
 * @returns Array of cached documents in the specified folder hierarchy
 * 
 * @example
 * ```ts
 * // Get root-level documents
 * const rootDocs = getCachedDocumentItemsByFolder(gameId);
 * 
 * // Get documents in a specific folder (including subfolders)
 * const folderDocs = getCachedDocumentItemsByFolder(gameId, folderId);
 * ```
 */
export function getCachedDocumentItemsByFolder(gameId: string, folderId?: string | null): DocumentItem[] {
  const allItems = documentStore.getData();
  
  return allItems.filter(item => {
    // Filter by game
    if (item.game_id !== gameId) return false;
    
    // Handle folder filtering
    if (isInRoot(folderId)) {
      return isInRoot(item.folder);
    } else {
      const allFolderIds = getAllSubfolderIds(gameId, 'documents', folderId!);
      return isInFolderSet(item.folder, allFolderIds);
    }
  });
}
